import { useEffect } from "react";
import { Link } from "react-router-dom";
import { BookOpen, LifeBuoy, X } from "lucide-react";
import { DropdownCard, type StatusItem } from "./DropdownCard";
import { Button } from "../ui";

interface HelpAndSupportProps {
  onButtonClick: () => void;
}

const propertyStatus: StatusItem[] = [
  {
    type: "Open",
    definition:
      "The property is available and actively accepting offers or inquiries. It has not entered into any formal agreement.",
  },
  {
    type: "Pending",
    definition:
      "The seller has accepted an offer, but the deal has not yet moved into escrow. Backup offers may still be considered.",
  },
  {
    type: "In Escrow",
    definition:
      "A purchase agreement is signed and the transaction is in escrow while inspections, financing and title work are completed.",
  },
  {
    type: "Closed",
    definition: "The sale is complete and ownership has transferred to the buyer.",
  },
  {
    type: "Canceled",
    definition:
      "The listing or transaction was withdrawn or fell through and the property is no longer being marketed.",
  },
];

const propertyMetrics: StatusItem[] = [
  {
    type: "Sent",
    definition:
      "How many buyers the property has been sent or distributed to — the number of people or parties who received the listing details.",
  },
  {
    type: "Open",
    definition:
      "How many recipients opened or viewed the property details after receiving them.",
  },
  {
    type: "Bid",
    definition: "How many offers buyers have submitted on the property.",
  },
];

export default function HelpAndSupport({ onButtonClick }: HelpAndSupportProps) {
  // Escape closes the panel, like any other dialog
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onButtonClick();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onButtonClick]);

  return (
    <div className="flex min-h-full flex-col">
      {/* Header — sticks while the panel scrolls; bleeds over the parent's px-6 */}
      <header className="sticky top-0 z-10 -mx-6 flex items-start justify-between gap-4 border-b border-gray-100 bg-white/95 px-6 py-5 backdrop-blur">
        <div className="flex items-center gap-3">
          <span className="flex size-9 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
            <LifeBuoy aria-hidden className="size-4" />
          </span>
          <div>
            <h2 className="text-sm font-semibold tracking-tight text-gray-900">Help &amp; support</h2>
            <p className="text-meta text-gray-500">Quick reference for statuses and metrics</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onButtonClick}
          aria-label="Close help and support"
          autoFocus
        >
          <X />
        </Button>
      </header>

      <div className="flex-1 space-y-8 py-6">
        <section aria-labelledby="help-status-heading">
          <h3 id="help-status-heading" className="text-sm font-semibold text-gray-900">
            Property statuses
          </h3>
          <p className="mb-3 mt-1 text-meta text-gray-500">
            The statuses you can filter listings and deals by.
          </p>
          <DropdownCard items={propertyStatus} defaultOpenIndex={0} headingLevel={4} />
        </section>

        <section aria-labelledby="help-metrics-heading">
          <h3 id="help-metrics-heading" className="text-sm font-semibold text-gray-900">
            Engagement metrics
          </h3>
          <p className="mb-3 mt-1 text-meta text-gray-500">
            How we measure buyer engagement on a property.
          </p>
          <DropdownCard items={propertyMetrics} defaultOpenIndex={0} headingLevel={4} />
        </section>
      </div>

      <footer className="mb-6 rounded-2xl bg-gray-50 p-5 ring-1 ring-inset ring-gray-200">
        <div className="flex items-start gap-3">
          <BookOpen aria-hidden className="mt-0.5 size-4 shrink-0 text-gray-400" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900">Need more help?</p>
            <p className="mt-1 text-meta leading-relaxed text-gray-600">
              Browse answers for buyers, sellers, agents and wholesalers, or join the FlipIQ
              Marketplace weekly Q&amp;A call.
            </p>
            <Link
              to="/info/faqs"
              onClick={onButtonClick}
              className="mt-3 inline-flex items-center gap-1 text-meta font-medium text-brand-700 hover:underline"
            >
              Read the FAQs
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
